import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../../lib/api-client';
import { logger } from '../../utils/logger';

interface ReferralState {
  referralCode: string;
  referralLink: string;
  settings: any;
  rewards: any[];
  referrals: any[];
  earnings: any[];
  statistics: {
    totalReferrals: number;
    activeReferrals: number;
    premiumConversions: number;
    totalEarned: number;
    pendingEarnings: number;
    redeemable: number;
    referralPoints: number;
  };
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  cacheValid: boolean;
}

const initialState: ReferralState = {
  referralCode: '',
  referralLink: '',
  settings: null,
  rewards: [],
  referrals: [],
  earnings: [],
  statistics: {
    totalReferrals: 0,
    activeReferrals: 0,
    premiumConversions: 0,
    totalEarned: 0,
    pendingEarnings: 0,
    redeemable: 0,
    referralPoints: 0,
  },
  loading: false,
  error: null,
  lastFetched: null,
  cacheValid: false,
};

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

// Async thunks
export const fetchReferralData = createAsyncThunk(
  'referral/fetchData',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const referralState = state.referral;

      // Check if cache is still valid AND has actual data
      if (
        !forceRefresh &&
        referralState.cacheValid &&
        referralState.lastFetched &&
        referralState.referralCode && // Must have actual referral code
        Date.now() - referralState.lastFetched < CACHE_DURATION
      ) {
        logger.log('✅ Using cached referral data');
        return null; // Return null to indicate cached data is being used
      }

      logger.log('🔄 Fetching fresh referral data from API');

      // Fetch all data in parallel
      const [publicInfoRes, myReferralsRes, statisticsRes, walletRes] = await Promise.allSettled([
        apiClient.getReferralPublicInfo(),
        apiClient.getMyReferrals(),
        apiClient.getReferralStatistics(),
        apiClient.getWalletBalance(),
      ]);

      const data: any = {
        settings: null,
        rewards: [],
        referralCode: '',
        referrals: [],
        earnings: [],
        statistics: {
          totalReferrals: 0,
          activeReferrals: 0,
          premiumConversions: 0,
          totalEarned: 0,
          pendingEarnings: 0,
          redeemable: 0,
          referralPoints: 0,
        },
      };

      // Process public info
      if (publicInfoRes.status === 'fulfilled' && publicInfoRes.value.success) {
        data.settings = publicInfoRes.value.data.settings;
        data.rewards = publicInfoRes.value.data.rewards || [];
      }

      // Process my referrals
      if (myReferralsRes.status === 'fulfilled' && myReferralsRes.value.success) {
        const myData = myReferralsRes.value.data;
        logger.log('🔍 My Referrals API Response:', myData);
        logger.log('📋 Referral Code from API:', myData.referralCode);
        data.referralCode = myData.referralCode || '';
        data.referrals = myData.referrals || [];
        data.earnings = myData.earnings || [];
        data.statistics.totalReferrals = myData.totalReferrals || 0;
        data.statistics.totalEarned = myData.totalEarnings || 0;
        data.statistics.pendingEarnings = myData.pendingEarnings || 0;
      } else {
        logger.error('❌ My Referrals API failed:', myReferralsRes);
        data.referralCode = '';
        data.referrals = [];
        data.earnings = [];
      }

      // Process statistics
      if (statisticsRes.status === 'fulfilled' && statisticsRes.value.success) {
        const stats = statisticsRes.value.data;
        data.statistics.premiumConversions = stats.successfulReferrals || 0;
        data.statistics.activeReferrals = stats.totalReferrals || data.statistics.totalReferrals;
      }

      // Process wallet
      if (walletRes.status === 'fulfilled' && walletRes.value.success) {
        data.statistics.redeemable = walletRes.value.data.balance || 0;
      }

      return data;
    } catch (error: any) {
      logger.error('Error fetching referral data:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to load referral data');
    }
  }
);

const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {
    clearReferralData: (state) => {
      return { ...initialState };
    },
    invalidateCache: (state) => {
      state.cacheValid = false;
    },
    updateReferralCode: (state, action: PayloadAction<string>) => {
      state.referralCode = action.payload;
      state.referralLink = `${window.location.origin}/register?ref=${action.payload}`;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReferralData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReferralData.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          // Update with fresh data
          state.settings = action.payload.settings;
          state.rewards = action.payload.rewards;
          state.referralCode = action.payload.referralCode;
          state.referralLink = `${window.location.origin}/register?ref=${action.payload.referralCode}`;
          state.referrals = action.payload.referrals;
          state.earnings = action.payload.earnings;
          state.statistics = action.payload.statistics;
          state.lastFetched = Date.now();
          state.cacheValid = true;
        }
        // If action.payload is null, we're using cached data (no update needed)
      })
      .addCase(fetchReferralData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Don't invalidate cache on error - keep showing old data
      });
  },
});

export const { clearReferralData, invalidateCache, updateReferralCode } = referralSlice.actions;
export default referralSlice.reducer;